/**
 * Authentication Store for Nour Al-Arabia
 *
 * Manages authentication state with:
 * - User info persisted to localStorage (for UX)
 * - Access token kept in memory only (for security)
 * - Auto-refresh on app initialization
 * - Role-based permission helpers
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  setAccessToken,
  clearAccessToken,
  setLocaleGetter,
  setLogoutHandler,
  clearQueryCache,
} from '@/services/api';
import { authApi } from '@/services/authApi';
import { useAppStore } from '@/stores/appStore';
import { destroyGlobalAudioElement } from '@/components/content/MiniPlayer/MiniPlayer';
import type { UserInfo, UserRole, LoginRequest, ApiError } from '@/types/api';

// =============================================================================
// Types
// =============================================================================

/** Role hierarchy levels (higher = more permissions) */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  STUDENT: 1,
  TEACHER: 2,
  ADMIN: 3,
} as const;

/** Auth state managed by the store */
interface AuthState {
  /** Current authenticated user (persisted to localStorage) */
  user: UserInfo | null;
  /** JWT access token (memory only, NOT persisted) */
  accessToken: string | null;
  /** Whether a login/logout operation is in progress */
  isLoading: boolean;
  /** Whether auth state has been checked on app load */
  isInitialized: boolean;
  /** Last authentication error */
  error: ApiError | null;
  /** Whether user must change password before accessing the app (persisted) */
  forcePasswordChange: boolean;
}

/** Auth actions available on the store */
interface AuthActions {
  /** Login with username and password - returns user info for immediate use */
  login: (credentials: LoginRequest) => Promise<UserInfo>;
  /** Logout the current user */
  logout: () => Promise<void>;
  /** Update the access token (used by refresh) */
  setAccessToken: (token: string | null) => void;
  /** Initialize auth state on app load */
  initialize: () => Promise<void>;
  /** Clear any authentication error */
  clearError: () => void;
  /** Clear the force password change flag after successful password change */
  clearForcePasswordChange: () => void;
  /** Internal: Set user directly (for testing) */
  _setUser: (user: UserInfo | null) => void;
  /** Internal: Reset store (for testing) */
  _reset: () => void;
}

/** Combined auth store type */
export type AuthStore = AuthState & AuthActions;

// =============================================================================
// Initial State
// =============================================================================

const initialState: AuthState = {
  user: null,
  accessToken: null,
  isLoading: false,
  isInitialized: false,
  error: null,
  forcePasswordChange: false,
};

// =============================================================================
// Store Implementation
// =============================================================================

/**
 * Authentication store using Zustand
 *
 * Security considerations:
 * - Access token is kept in memory only (not persisted to localStorage)
 * - User info is persisted for UX (prevents flash of login screen)
 * - On app load, if user exists, we attempt token refresh
 * - On refresh failure, user is cleared
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      ...initialState,

      // =======================================================================
      // Actions
      // =======================================================================

      /**
       * Login with username and password
       * - Calls login API
       * - Stores access token in memory
       * - Stores user info in state (persisted)
       * - Handles forcePasswordChange flag from response
       * - Returns user info for immediate use (avoids stale state issues)
       */
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.login(credentials);

          // Store token in memory via API client
          setAccessToken(response.accessToken);

          // Check for forcePasswordChange in response (defaults to false if not present)
          const forcePasswordChange = (response as { forcePasswordChange?: boolean }).forcePasswordChange ?? false;

          // Update state
          set({
            user: response.user,
            accessToken: response.accessToken,
            isLoading: false,
            error: null,
            forcePasswordChange,
          });

          // Return user for immediate use (state may be stale in calling component)
          return response.user;
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
            user: null,
            accessToken: null,
            forcePasswordChange: false,
          });
          // Re-throw for component error handling
          throw error;
        }
      },

      /**
       * Logout the current user
       * - Calls logout API (invalidates refresh token server-side)
       * - Clears all local auth state
       */
      logout: async () => {
        set({ isLoading: true });

        try {
          await authApi.logout();
        } catch {
          // Continue with local logout even if API call fails
          // The server session will expire anyway
        } finally {
          // Always clear local state
          clearAccessToken();
          // Clear cached React Query data so the next user on a shared/school
          // device cannot see the previous user's data (fe-auth-client-01).
          clearQueryCache();
          // Clean up global audio element to prevent memory leaks
          destroyGlobalAudioElement();
          set({
            ...initialState,
            isInitialized: true, // Remain initialized after logout
          });
        }
      },

      /**
       * Update the access token
       * Used by token refresh mechanism
       */
      setAccessToken: (token: string | null) => {
        setAccessToken(token);
        set({ accessToken: token });
      },

      /**
       * Initialize auth state on app load
       * - Should be called once on app mount
       * - If user exists in localStorage, attempts token refresh
       * - On success: user stays logged in
       * - On failure: clears user (session expired)
       */
      initialize: async () => {
        const state = get();

        // Already initialized, skip
        if (state.isInitialized) {
          return;
        }

        // Set up API client integrations
        setLocaleGetter(() => useAppStore.getState().locale);
        setLogoutHandler(() => {
          // Clear auth state when API detects auth failure
          clearAccessToken();
          // Clean up global audio element to prevent memory leaks
          destroyGlobalAudioElement();
          set({
            user: null,
            accessToken: null,
            error: null,
          });
        });

        // No user in storage, nothing to refresh
        if (!state.user) {
          set({ isInitialized: true });
          return;
        }

        // User exists - attempt token refresh
        set({ isLoading: true });

        try {
          const response = await authApi.refresh();

          // Refresh succeeded - update token
          setAccessToken(response.accessToken);
          set({
            accessToken: response.accessToken,
            isLoading: false,
            isInitialized: true,
            error: null,
          });
        } catch {
          // Refresh failed - clear user (session expired)
          clearAccessToken();
          set({
            user: null,
            accessToken: null,
            isLoading: false,
            isInitialized: true,
            error: null, // Don't show error for expired session
          });
        }
      },

      /**
       * Clear any authentication error
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Clear the force password change flag
       * Called after user successfully changes their password
       */
      clearForcePasswordChange: () => {
        set({ forcePasswordChange: false });
      },

      /**
       * Internal: Set user directly (for testing)
       */
      _setUser: (user: UserInfo | null) => {
        set({ user });
      },

      /**
       * Internal: Reset store to initial state (for testing)
       */
      _reset: () => {
        clearAccessToken();
        set(initialState);
      },
    }),
    {
      name: 'nour-arabia-auth-storage',
      // Only persist user info and forcePasswordChange, NOT the access token (security)
      partialize: (state) => ({
        user: state.user,
        forcePasswordChange: state.forcePasswordChange,
      }),
    }
  )
);

// =============================================================================
// Selector Helpers
// =============================================================================

/**
 * Check if a role has at least the minimum required role level
 */
export function hasRoleLevel(userRole: UserRole | undefined, minRole: UserRole): boolean {
  if (!userRole) return false;
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[minRole];
}

/**
 * Check if a role matches any of the specified roles
 */
export function hasAnyRole(userRole: UserRole | undefined, roles: UserRole[]): boolean {
  if (!userRole) return false;
  return roles.includes(userRole);
}
